// frontend/src/App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ChatInterface from './components/ChatInterface';
import LoginPage from './pages/LoginPage';
import InstructorDashboard from './pages/InstructorDashboard';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/tailwind.css';

// Protected route component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  
  // If auth is still loading, show a loading indicator
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // If role is required and doesn't match, redirect
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={user.role === 'instructor' ? '/instructor' : '/'} />;
  }
  
  // User is authenticated with the correct role, render the children
  return children;
};

const App = () => {
  const { user, loading } = useAuth();

  // Redirect from the root path based on auth status
  const RootRedirect = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      );
    }
    
    if (!user) {
      return <Navigate to="/login" />;
    }
    
    if (user.role === 'instructor') {
      return <Navigate to="/instructor" />;
    }
    
    return <Navigate to="/chat" />;
  };
  
  return (
    <Router>
      <Routes>
        <Route path="/login" element={
          loading ? (
            <div className="flex justify-center items-center h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : user ? (
            <Navigate to={user.role === 'instructor' ? '/instructor' : '/chat'} />
          ) : (
            <LoginPage />
          )
        } />
        
        <Route path="/chat" element={
          <ProtectedRoute>
            <ChatInterface />
          </ProtectedRoute>
        } />
        
        <Route path="/instructor" element={
          <ProtectedRoute requiredRole="instructor">
            <InstructorDashboard />
          </ProtectedRoute>
        } />
        
        {/* Root route redirects based on auth status */}
        <Route path="/" element={<RootRedirect />} />
      </Routes>
    </Router>
  );
};

const AppWithAuth = () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);

export default AppWithAuth;